import React, { useEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";
import img_logo from "../../assets/logoIntellegnium.webp";
import "./nav.css";
import SmartImage from "../SmartImage";
import TrackedContactLink from "../TrackedContactLink";

export default function HeaderIntellegnium() {
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [hidden, setHidden] = useState(false);

  const lastScrollYRef = useRef(0);

  const scrollToSection = (href) => {
    if (href === "#inicio") {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      return;
    }

    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleNavClick = (e, href) => {
    e.preventDefault();
    scrollToSection(href);
    setOpen(false);
  };

  const openAboutModal = () => {
    setOpen(false);
    setAboutOpen(true);
  };

  const closeAboutModal = () => {
    setAboutOpen(false);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        closeAboutModal();
      }
    };

    if (aboutOpen) {
      window.addEventListener("keydown", onKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [aboutOpen]);

  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY || 0;
      const goingDown = currentY > lastScrollYRef.current;
      const shouldLockVisible = open || aboutOpen;

      if (shouldLockVisible) {
        setHidden(false);
      } else {
        setHidden(currentY > 80 && goingDown);
      }

      lastScrollYRef.current = currentY;
    };

    lastScrollYRef.current = window.scrollY || 0;
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, [open, aboutOpen]);

  useEffect(() => {
    if (open || aboutOpen) {
      setHidden(false);
    }
  }, [open, aboutOpen]);

  return (
    <>
      <header className={`site-nav ${hidden ? "site-nav--hidden" : ""}`}>
        <div className="menu">
          <button
            type="button"
            className="menu_logo menu_logoBtn"
            onClick={() => {
              scrollToSection("#inicio");
              setOpen(false);
            }}
            aria-label="Ir al inicio"
          >
            <SmartImage
              src={img_logo}
              alt="Intellegnium Diseñadores"
              className="menu_logoTexto"
              width="151"
              height="101"
              priority
            />
          </button>

          <nav className="menu_links">
            <a
              href="#inicio"
              className="link-nav"
              onClick={(e) => handleNavClick(e, "#inicio")}
            >
              INICIO
            </a>

            <a
              href="#servicios"
              className="link-nav"
              onClick={(e) => handleNavClick(e, "#servicios")}
            >
              SERVICIOS
            </a>

            <a
              href="#portafolio"
              className="link-nav"
              onClick={(e) => handleNavClick(e, "#portafolio")}
            >
              PORTAFOLIO
            </a>

            <button
              type="button"
              className="link-nav link-nav--button"
              onClick={openAboutModal}
            >
              NOSOTROS
            </button>

            <a
              href="#footer-contacto"
              className="link-nav"
              onClick={(e) => handleNavClick(e, "#footer-contacto")}
            >
              CONTACTO
            </a>
          </nav>
          <TrackedContactLink
            className="cta-btn desktop-cta"
            aria-label="Contactar por WhatsApp"
            eventLabel="floating_button"
            kind="whatsapp"
          >
            <span>Cotizar ahora</span>
          </TrackedContactLink>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="menu_toggle"
            aria-label="Abrir menú"
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {open && (
          <div className="mobile-menu">
            <a
              href="#inicio"
              onClick={(e) => handleNavClick(e, "#inicio")}
              className="mobile-link"
            >
              INICIO
            </a>

            <a
              href="#servicios"
              onClick={(e) => handleNavClick(e, "#servicios")}
              className="mobile-link"
            >
              SERVICIOS
            </a>

            <a
              href="#portafolio"
              onClick={(e) => handleNavClick(e, "#portafolio")}
              className="mobile-link"
            >
              PORTAFOLIO
            </a>

            <button
              type="button"
              onClick={openAboutModal}
              className="mobile-link mobile-link--button"
            >
              NOSOTROS
            </button>

            <a
              href="#footer-contacto"
              onClick={(e) => handleNavClick(e, "#footer-contacto")}
              className="mobile-link"
            >
              CONTACTO
            </a>

            <TrackedContactLink
              className="cta-btn mobile-cta"
              aria-label="Contactar por WhatsApp"
              eventLabel="floating_button"
              kind="whatsapp"
            >
              <span>¿Cotizamos tu proyecto?</span>
            </TrackedContactLink>
          </div>
        )}
      </header>

      {aboutOpen && (
        <div className="about-modal" onClick={closeAboutModal}>
          <div
            className="about-modal__card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="about-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="about-modal__close"
              onClick={closeAboutModal}
              aria-label="Cerrar modal"
            >
              <X className="h-5 w-5" />
            </button>

            <p className="about-modal__eyebrow">NUESTRA HISTORIA</p>
            <h2 id="about-title" className="about-modal__title">
              Construimos ideas que se ven, se sienten y venden.
            </h2>

            <p className="about-modal__text">
              En Intellegnium Diseñadores creemos que una marca no solo debe
              verse bien, sino transmitir personalidad, conectar y permanecer en
              la mente de las personas.
            </p>

            <p className="about-modal__text">
              Desde 2019 hemos ayudado a negocios y emprendedores a construir
              una imagen sólida a través de diseño gráfico, branding,
              ilustración, animación, registro de marca, botargas y contenido
              estratégico. Con más de 250 clientes y más de 6 años de
              experiencia, trabajamos cada proyecto con enfoque creativo,
              atención al detalle y soluciones visuales pensadas para generar
              impacto real.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
